import axios, { AxiosInstance, AxiosError } from 'axios';
import { toast } from 'sonner';
import { getApiBaseUrl } from './config';
import { NETWORK_ERROR_MESSAGE, isNetworkError, notifyNetworkError } from './network-error';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}



const api: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 900000,
  headers: {
    'Content-Type': 'application/json',
  },
});



// Helper function to get error message
const getErrorMessage = (error: AxiosError): string => {
  if (error.response?.data) {
    const data = error.response.data as any;
    return data.message || data.error || 'Erro interno, aguarde alguns instantes.';
  }

  if (error.request) {
    return NETWORK_ERROR_MESSAGE;
  }

  return error.message || 'Erro desconhecido, aguarde alguns instantes.';
};



// Helper function to check if error is from AbortController
const isAbortError = (error: AxiosError): boolean => {
  return error.name === 'CanceledError' ||
         error.message === 'canceled' ||
         error.code === 'ERR_CANCELED' ||
         error.message?.includes('abort');
};



// GET/HEAD são leitura: falha por rede ali não perde nada da pessoa.
const isReadRequest = (config: { method?: string } | undefined): boolean => {
  const method = (config?.method ?? 'get').toLowerCase();
  return method === 'get' || method === 'head';
};



// Helper function to handle auto logout
const handleAutoLogout = () => {
  localStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_token');

  // Only redirect if not already on login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};



// Request Interceptor
api.interceptors.request.use(
  (config) => {
    // Adicionar token de autenticação
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);



// Response Interceptor with automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Try to refresh token on 401 if not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');

      if (refreshToken) {
        try {
          // Try to refresh the token
          const response = await api.post('/users/refresh-token', {
            refreshToken,
          });

          const { token, refreshToken: newRefreshToken } = response.data.data;

          // Update stored tokens
          const storage = localStorage.getItem('refresh_token') ? localStorage : sessionStorage;
          storage.setItem('auth_token', token);
          if (newRefreshToken) {
            storage.setItem('refresh_token', newRefreshToken);
          }

          // Update Authorization header and retry original request
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);

        } catch (refreshError) {
          // Refresh failed, logout
          handleAutoLogout();
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, logout
        handleAutoLogout();
        return Promise.reject(error);
      }
    }

    // Handle 402 (insufficient credits) - Use backend message
    if (error.response?.status === 402) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      return Promise.reject(error);
    }

    // Handle 403 (forbidden) - Use backend message when available
    if (error.response?.status === 403) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      return Promise.reject(error);
    }

    // Error handling global (skip for silent refresh attempts, aborted requests, and locally handled errors)
    if (!originalRequest._retry && !isAbortError(error) && !originalRequest._handleErrorLocally) {
      if (isNetworkError(error)) {
        // Rede caiu no meio da requisição. GET fica em silêncio: o React Query
        // retenta e o `QueryCache` (query-client.ts) só avisa se a queda durar
        // e a tela estiver vazia — polling em background nunca avisa. Escrita
        // é ação da pessoa e avisa na hora, com id fixo (um aviso por queda).
        if (!isReadRequest(originalRequest)) {
          notifyNetworkError();
        }
        return Promise.reject(error);
      }
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

// API Helper Functions with AbortController support
export const apiGet = async <T = any>(url: string, config?: { signal?: AbortSignal }): Promise<T> => {
  const response = await api.get<ApiResponse<T>>(url, config);
  return response.data.data!;
};

export const apiPost = async <T = any>(url: string, data?: any, config?: { signal?: AbortSignal; _handleErrorLocally?: boolean }): Promise<T> => {
  const response = await api.post<ApiResponse<T>>(url, data, config);
  return response.data.data!;
};

export const apiPut = async <T = any>(url: string, data?: any, config?: { signal?: AbortSignal }): Promise<T> => {
  const response = await api.put<ApiResponse<T>>(url, data, config);
  return response.data.data!;
};

export const apiPatch = async <T = any>(url: string, data?: any, config?: { signal?: AbortSignal }): Promise<T> => {
  const response = await api.patch<ApiResponse<T>>(url, data, config);
  return response.data.data!;
};

export const apiDelete = async (
  url: string,
  config?: { signal?: AbortSignal; data?: unknown }
): Promise<void> => {
  await api.delete(url, config);
};

export { api };